import { Suspense, useEffect, useState, useRef, useMemo } from "react";
import { flushSync } from "react-dom";
import {
  Outlet,
  useNavigate,
  useLocation,
  Link,
  useMatches,
} from "react-router-dom";

import { useCharactersStore } from "@/store/useCharactersStore";
import { usePaymentsStore } from "@/store/usePaymentsStore";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  consumePendingRedeemNavigationPath,
  getDeferredDeepLinkFromFirebase,
  hasPendingRedeemNavigationIntent,
} from "@/utils/deepLinkUtils";
import { logException, isOnboardingProgress, getPlatformExperience } from "@um/ui";
import {
  useAuthStore,
  useAuthInit,
  selectAccountSettingsLoaded,
} from "@um/ui";
import {
  initializeAutoUpdate,
  applyPendingUpdate,
  compareToMinVersion,
} from "@/services/autoUpdateService";
import { scheduleStorageFootprintReport } from "@/services/storageFootprint";
import { Capacitor } from "@capacitor/core";
import { storeLinks, waitForState } from "@um/ui";
import { useAutoUpdateStore } from "@/store/useAutoUpdateStore";
import { deviceInfoService } from "@um/ui";
import AuthPanel from "./auth/AuthPanel";
import { getUserRole } from "@um/contracts";
import { enableSocialEvents } from "@/services/social";
import { App as CapacitorApp } from "@capacitor/app";
import {
  pendingWebHandoff,
  resumeWebOnboardingHandoff,
} from "@/services/webOnboardingHandoff";
import {
  clearWebHandoffIntent,
  queuedWebHandoff,
  webHandoffIntent,
  webHandoffId,
  accountWebHandoff,
  webHandoffIsComplete,
  WEB_HANDOFF_LINK_EVENT,
} from "@/services/webOnboardingSession";

/**
 * Suspense boundary for the lazily-loaded route chunks (see App.tsx). The
 * fallback is deliberately blank rather than a spinner: chunks are served from
 * the same origin and usually resolve in well under a frame, and flashing a
 * spinner in between is worse than showing nothing.
 */
function RouteOutlet() {
  return (
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
  );
}

// Types that only exist for initialization
interface InitStep {
  id: string;
  label: string;
  check: () => Promise<any>;
}

export default function AppInitializer() {
  const navigate = useNavigate();
  const location = useLocation();
  const matches = useMatches();
  // Dev Settings can run the app journey in a browser against emulators.
  // Exercise the same handoff routing there; native capabilities stay native.
  const supportsWebHandoff =
    Capacitor.isNativePlatform() ||
    ((import.meta.env.DEV || import.meta.env.VITE_USE_EMULATORS === "true") &&
      getPlatformExperience() === "app");

  // Auth and data stores
  useAuthInit();

  // Note: We use useCharactersStore.getState().checkHasCharacters() directly in steps
  // to avoid stale closures in the async check functions
  const { initialize: initializeAutoUpdateStore, downloadUpdate } =
    useAutoUpdateStore();

  const [showAuthDrawer, setShowAuthDrawer] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [redirectToOnboarding, setRedirectToOnboarding] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationRunning, setInitializationRunning] = useState(false);
  const [handoffChecked, setHandoffChecked] = useState(
    !supportsWebHandoff
  );
  const attemptedHandoffRef = useRef<string>();
  const [handoffProblem, setHandoffProblem] = useState<
    "retry" | "update-required" | "recovery-required"
  >();
  const [handoffBlocked, setHandoffBlocked] = useState(false);
  const [handoffWake, setHandoffWake] = useState(0);
  const routeRef = useRef(location.pathname);
  const interactionRef = useRef(0);
  const flowEngagedRef = useRef(false);
  const aliveRef = useRef(true);
  if (routeRef.current !== location.pathname) {
    routeRef.current = location.pathname;
    flowEngagedRef.current = false;
    interactionRef.current++;
  }
  const handoffIntent = () =>
    webHandoffIntent(location.search) || queuedWebHandoff();
  const canResumeHandoff = () =>
    aliveRef.current &&
    !hasPendingRedeemNavigationIntent().skip &&
    (routeRef.current === "/" ||
      (routeRef.current === "/onboarding" && !flowEngagedRef.current));

  useEffect(() => {
    aliveRef.current = true;
    const interacted = () => {
      interactionRef.current++;
      if (routeRef.current === "/onboarding") flowEngagedRef.current = true;
    };
    for (const event of ["pointerdown", "keydown", "input"])
      document.addEventListener(event, interacted, true);
    return () => {
      aliveRef.current = false;
      for (const event of ["pointerdown", "keydown", "input"])
        document.removeEventListener(event, interacted, true);
    };
  }, []);
  const [updateStatus, setUpdateStatus] = useState<{
    isIncompatible?: boolean;
    meetsMinVersion?: boolean;
    versionNotAvailable?: boolean;
    isDownloading?: boolean;
    isApplying?: boolean;
    updateVersion?: string;
  }>();
  const lastCompletedStepIndexRef = useRef(-1);

  const requiresAuth =
    matches.some(
      (match) => (match.handle as { requiresAuth: boolean })?.requiresAuth
    ) ||
    (supportsWebHandoff && !!handoffIntent());

  // Public routes (sharedPublicRoutes) opt out of the forced-onboarding redirect
  // and render immediately during init instead of the loading gate.
  const isPublicRoute = matches.some(
    (match) => (match.handle as { publicRoute?: boolean })?.publicRoute
  );

  // Steps are derived from current route/auth context
  // Use memo to avoid re-creating on every render while keeping values fresh
  const initSteps = useMemo<InitStep[]>(() => {
    console.log(
      "[AppInitializer] Route requires auth:",
      requiresAuth,
      "path:",
      location.pathname
    );

    const steps: InitStep[] = [
      {
        id: "initializeFirebaseAuth",
        label: "Loading...",
        check: async () => {
          // Fire-and-forget: analytics should never block the app from loading.
          // On native, AppsFlyer.initSDK can hang on WebView reloads because
          // the native SDK is already initialized but the JS bridge is fresh.
          enableSocialEvents().catch((e) => logException(e));

          console.log("[AppInitializer] Waiting for auth to initialize...");
          const AUTH_TIMEOUT_MS = 15_000;
          const authReady = waitForState(
            useAuthStore,
            ({ initializing }) => !initializing
          );
          const timeout = new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error("AUTH_INIT_TIMEOUT")),
              AUTH_TIMEOUT_MS
            )
          );
          try {
            await Promise.race([authReady, timeout]);
          } catch (e) {
            if (e instanceof Error && e.message === "AUTH_INIT_TIMEOUT") {
              console.warn(
                "[AppInitializer] Auth init timed out after",
                AUTH_TIMEOUT_MS,
                "ms — continuing with current state. Store:",
                useAuthStore.getState()
              );
              // Don't throw — let subsequent steps use whatever auth state is available
            } else {
              throw e;
            }
          }
        },
      },
      {
        id: "updates",
        label: "Loading...",
        check: async () => {
          console.log("[AppInitializer] Checking for updates...");

          // A handoff's minimum version is enforced only after server validation.
          const minVersion = webHandoffIntent(location.search)
            ? null
            : new URLSearchParams(location.search).get("minVersion");

          const autoUpdateState = useAutoUpdateStore.getState();
          if (autoUpdateState.isInitialized === undefined) {
            await initializeAutoUpdateStore();
          } else if (autoUpdateState.isInitialized === false) {
            await waitForState(
              useAutoUpdateStore,
              ({ isInitialized }) => !!isInitialized
            );
          }

          // Deferred storage-footprint telemetry (native only, once per
          // session) — after update init so bundle state is settled.
          scheduleStorageFootprintReport();

          if (minVersion) {
            console.log(
              "[AppInitializer] Minimum version requirement:",
              minVersion
            );
          }

          // Use the auto-update service to initialize and check for updates
          const result = await initializeAutoUpdate(minVersion);

          if (result.meetsMinVersion && result.bundle) {
            console.log("[AppInitializer] Available bundle is forced update");
            setUpdateStatus({
              isDownloading: true,
              updateVersion: result.bundle.version,
            });
            await downloadUpdate(result.bundle);
            setUpdateStatus({
              isApplying: true,
              updateVersion: result.bundle.version,
            });
            await applyPendingUpdate();
            return result;
          }

          // Only set status if we're not going to poll for download completion
          setUpdateStatus({
            isIncompatible: result.isIncompatible,
            meetsMinVersion: result.meetsMinVersion,
            versionNotAvailable: result.versionNotAvailable,
          });

          // Handle different scenarios
          if (result.isIncompatible) {
            console.log(
              "[AppInitializer] Version is incompatible, store update required"
            );
            // Will show store update UI, stop initialization
            // TODO: Show a pretty UI asking the user to update the app
            throw new Error("INCOMPATIBLE_VERSION");
          }

          if (result.versionNotAvailable && minVersion) {
            console.log("[AppInitializer] Required version not available");
            // Will show version not available UI, stop initialization
            throw new Error("VERSION_NOT_AVAILABLE");
          }

          // If we have a minVersion but don't meet it and no update available, stop
          if (minVersion && !result.meetsMinVersion) {
            throw new Error("VERSION_NOT_AVAILABLE");
          }

          return result;
        },
      },
      {
        id: "auth",
        label: "Verifying authentication...",
        check: async () => {
          console.log("[AppInitializer] Checking authentication...");

          if (!requiresAuth) {
            return;
          }

          // get the user data after the store is done initializing to determine if we are logged in
          console.log("[AppInitializer] wait for auth initialization:");
          const { userData: currentUserData, user: firebaseUser } =
            await waitForState(
              useAuthStore,
              ({ initializing }) => !initializing
            );

          console.log(
            "[AppInitializer] Auth state - initializing:",
            "userData:",
            currentUserData,
            "isAnonymous:",
            firebaseUser?.isAnonymous
          );

          const isAuthenticated = !!currentUserData;
          const isAnonymous = firebaseUser?.isAnonymous ?? false;

          // Show auth panel if not authenticated OR if anonymous user on auth-required page
          // Anonymous users should see the sign-in options so they can either:
          // - Sign in with Google/Apple
          // - Continue as "I'm a new user" which redirects to onboarding
          if (!isAuthenticated || isAnonymous) {
            console.log(
              "[AppInitializer] User not authenticated or is anonymous, showing auth panel"
            );
            return { authenticated: false, showAuthPanel: true, isAnonymous };
          }

          console.log("[AppInitializer] User authenticated:", currentUserData);
          return { authenticated: true };
        },
      },
      {
        id: "webOnboardingHandoff",
        label: "Continuing your setup...",
        check: async () => {
          if (
            !supportsWebHandoff ||
            isPublicRoute ||
            !useAuthStore.getState().user ||
            useAuthStore.getState().user?.isAnonymous ||
            hasPendingRedeemNavigationIntent().skip
          ) {
            setHandoffChecked(true);
            return { handoffStatus: "not-applicable" };
          }
          const intent = handoffIntent();
          const candidate = intent || pendingWebHandoff();
          const original = useAuthStore.getState();
          const currentHandoff = accountWebHandoff(
            original.accountId,
            original.user?.uid,
            original.accountSettings,
            original.userData?.webOnboardingHandoff
          );
          const discover =
            !selectAccountSettingsLoaded(original) ||
            (currentHandoff &&
              !webHandoffIsComplete(currentHandoff, original.accountSettings)) ||
            original.accountSettings?.createdDefaultCharacter !== true;
          if ((!candidate && !discover) || !canResumeHandoff()) {
            setHandoffChecked(true);
            return { handoffStatus: candidate ? "deferred" : "none" };
          }
          if (candidate) attemptedHandoffRef.current = webHandoffId(candidate);
          const interaction = interactionRef.current;
          const result = await resumeWebOnboardingHandoff({
            intent,
            discover,
            isAllowed: () =>
              canResumeHandoff() && interactionRef.current === interaction,
          });
          console.log("[AppInitializer] Web handoff check", {
            accountId: original.accountId,
            source: intent
              ? "link"
              : candidate
                ? "local-marker"
                : "server-discovery",
            result: result.status,
          });
          setHandoffChecked(true);
          if (result.status === "discovery-unavailable") {
            // No handoff was identified. An optional server lookup must not
            // lock out a returning account with a missing completion flag.
            // Continue to the normal settings/progress check, not directly Home.
            setHandoffProblem(undefined);
            setHandoffBlocked(false);
            return { handoffStatus: result.status };
          }
          if (
            result.status === "retry" ||
            result.status === "recovery-required" ||
            result.status === "update-required"
          ) {
            setHandoffProblem(result.status);
            // Settings may have finished loading during the request. Check the
            // current state, while retaining identity/account-switch protection.
            const current = useAuthStore.getState();
            const currentSession = accountWebHandoff(
              current.accountId,
              current.user?.uid,
              current.accountSettings,
              current.userData?.webOnboardingHandoff
            );
            const canKeepAccount =
              !!original.accountId &&
              current.accountId === original.accountId &&
              result.accountId !== current.accountId &&
              (!currentSession ||
                webHandoffIsComplete(currentSession, current.accountSettings)) &&
              current.user?.uid === original.user?.uid &&
              selectAccountSettingsLoaded(current) &&
              current.accountSettings?.createdDefaultCharacter === true &&
              !isOnboardingProgress(current.accountSettings.onboardingProgress);
            setHandoffBlocked(!canKeepAccount);
            return {
              handoffStatus: result.status,
              handoffBlocked: !canKeepAccount,
              handoffFallback: canKeepAccount,
            };
          }
          if (result.status === "resumed") {
            clearWebHandoffIntent();
            setHandoffProblem(undefined);
            setHandoffBlocked(false);
            return result.destination === "/onboarding"
              ? { showOnboarding: true, redirectToOnboarding: true }
              : { handoffDestination: "/" };
          }
          if (result.status === "deferred") {
            attemptedHandoffRef.current = undefined;
            return { handoffStatus: result.status };
          }
          // Invalid/revoked links never impose a version gate or select an account.
          if (intent) {
            clearWebHandoffIntent();
            if (webHandoffIntent(location.search)) {
              const params = new URLSearchParams(location.search);
              for (const key of [
                "webOnboardingAccount",
                "webOnboardingCharacter",
                "minVersion",
              ])
                params.delete(key);
              navigate(
                { pathname: location.pathname, search: params.toString() },
                { replace: true }
              );
            }
          }
          return { handoffStatus: result.status };
        },
      },
      {
        id: "deviceInfo",
        label: "Syncing device info...",
        check: async () => {
          if (!requiresAuth && !useAuthStore.getState().userData?.uid) {
            return;
          }

          const currentUserData = useAuthStore.getState().userData;

          if (!currentUserData) {
            return { deviceInfoUpdated: false };
          }
          deviceInfoService
            .updateDeviceInfo(
              currentUserData.uid,
              useAutoUpdateStore.getState().currentBuild,
              useAutoUpdateStore.getState().currentBundle
            )
            .catch((error) => {
              logException(error);
              console.warn(
                "[AppInitializer] Failed to update device info on sign-in:",
                error
              );
            });

          return { deviceInfoUpdated: true };
        },
      },
      {
        id: "updateUserSelection",
        label: "Syncing profile selection...",
        check: async () => {
          if (!requiresAuth && !useAuthStore.getState().userData?.uid) {
            return;
          }

          const { userData, userMindOverride, account, user } =
            useAuthStore.getState();

          if (!userData) {
            return { userSelectionUpdated: false };
          }

          const role = userMindOverride
            ? "member"
            : getUserRole(account, user?.uid);

          deviceInfoService
            .updateUserSelection(userMindOverride, role, userData.uid)
            .catch((error) => {
              logException(error);
              console.warn(
                "[AppInitializer] Failed to update user selection:",
                error
              );
            });

          return { userSelectionUpdated: true };
        },
      },
      {
        id: "checkNotificationPermissions",
        label: "Checking notification settings...",
        check: async () => {
          // Skip notification setup during onboarding - it's handled later in the flow
          if (location.pathname === "/onboarding") {
            console.log(
              "[AppInitializer] Skipping notification check during onboarding"
            );
            return { notificationPermissionsChecked: false, skipped: true };
          }

          if (!requiresAuth && !useAuthStore.getState().userData?.uid) {
            return;
          }

          const currentUserData = useAuthStore.getState().userData;
          const settings = useAuthStore.getState().settings;

          // Skip for users who haven't completed onboarding yet.
          // The onboarding flow has its own notification step (enable-notifications).
          // Without this guard, new users on platforms where permissions are auto-granted
          // (e.g. Android < 13) get acknowledgedNotifications set to true before they
          // ever see the onboarding notification screen.
          if (!settings.createdDefaultCharacter) {
            console.log(
              "[AppInitializer] Skipping notification check - user hasn't completed onboarding"
            );
            return { notificationPermissionsChecked: false, skipped: true };
          }

          if (!currentUserData) {
            return;
          }

          // Always check permissions and update token if granted
          // Also set acknowledgedNotifications to prevent showing dialog to users who already enabled
          try {
            const { firebaseMessagingService } =
              await import("@/services/firebaseMessagingService");
            const permissionStatus =
              await firebaseMessagingService.getPermissionStatus();

            console.log(
              "[AppInitializer] Notification permission status:",
              permissionStatus
            );

            if (permissionStatus === "granted") {
              console.log(
                "[AppInitializer] Notifications already enabled, updating token and settings"
              );

              if (!settings.acknowledgedNotifications) {
                await useAuthStore
                  .getState()
                  .updateSettings({ acknowledgedNotifications: true });
              }

              // Request and save token (this ensures token is up-to-date in Device document)
              await firebaseMessagingService.requestPermissionAndSaveToken(
                currentUserData.uid
              );
            }
          } catch (error) {
            logException(error);
            console.warn(
              "[AppInitializer] Failed to check/update notification permissions:",
              error
            );
          }

          return { notificationPermissionsChecked: true };
        },
      },
      {
        id: "characterOnboarding",
        label: "Loading your profile...",
        check: async () => {
          console.log("[AppInitializer] charaterOnboarding step - checking...");
          if (!requiresAuth && !useAuthStore.getState().userData?.uid) {
            console.log(
              "[AppInitializer] charaterOnboarding - skipping (no auth required or no user)"
            );
            return;
          }

          const currentPath = window.location.pathname;

          // Never hijack a public route with the forced-onboarding redirect.
          // These pages (explore, view, space, privacy, redeem, etc.) handle
          // their own UX; a logged-in user landing here shouldn't be bounced.
          if (isPublicRoute) {
            console.log(
              "[AppInitializer] On public route, skipping onboarding check",
              { currentPath }
            );
            return { showOnboarding: false, redirectToOnboarding: false };
          }

          // If already on /onboarding, skip this check entirely
          // HybridFlowPage handles its own progress recovery
          if (currentPath === "/onboarding") {
            console.log(
              "[AppInitializer] Already on /onboarding, skipping onboarding check"
            );
            return { showOnboarding: false, redirectToOnboarding: false };
          }

          // Don't hijack the /redeem flow — the redeem page handles its own
          // navigation (and reloads to "/" on completion, where onboarding
          // will be evaluated normally). Use live pathname + pending intent
          // (push tap / OTA restore) because init steps can capture stale routes.
          const redeemIntent = hasPendingRedeemNavigationIntent();
          if (redeemIntent.skip) {
            consumePendingRedeemNavigationPath();
            console.log(
              "[AppInitializer] Redeem navigation intent detected, skipping onboarding redirect",
              { reason: redeemIntent.reason, currentPath }
            );
            return { showOnboarding: false, redirectToOnboarding: false };
          }

          // Wait for the account's settings document to actually load before
          // deciding anything. `authState.settings` is a COMPUTED merge
          // (account + local + device) that is never undefined, so it cannot
          // tell "not loaded yet" from "loaded and empty" — reading it early
          // reports no onboardingProgress and drops the user through to the
          // hasCharacters heuristic below. On a fresh install resuming a web
          // onboarding session that means: progress exists at
          // post-character-creation, the web session already created the
          // character, so hasCharacters is true → "onboarding done" → Home
          // with no Mind (UM-287). Gate on the RAW slice instead, the same way
          // HybridFlowPage does before starting a flow.
          if (useAuthStore.getState().accountId) {
            try {
              // `accountSettingsLoaded`, not `accountSettings !== undefined`:
              // after a fresh sign-in the computed accountId flips to the new
              // account the moment userData arrives, while accountSettings
              // still holds the PREVIOUS account's value until a React effect
              // clears it. `null !== undefined` satisfied the old check, so
              // this step read "no saved progress", fell through to the
              // hasCharacters heuristic, and sent a user resuming web
              // onboarding in the app to Home instead of /onboarding.
              await waitForState(
                useAuthStore,
                selectAccountSettingsLoaded,
                5000
              );
            } catch {
              console.warn(
                "[AppInitializer] charaterOnboarding - account settings did not load in time; routing to /onboarding to decide there"
              );
              // Send them to /onboarding rather than falling through. Both
              // outcomes are guesses, but only this one self-corrects:
              // HybridFlowPage waits for `accountSettings !== undefined`
              // before starting, resumes saved progress if there is any, and
              // redirects to "/" when onboarding is already complete. Falling
              // through would land on Home and re-create UM-287.
              return { showOnboarding: true, redirectToOnboarding: true };
            }
          }

          const authState = useAuthStore.getState();
          const accountId = authState.accountId;
          // Read the raw account settings slice, not the computed merge.
          const savedProgress = authState.accountSettings?.onboardingProgress;
          const completedWebSetup = accountWebHandoff(
            accountId,
            authState.user?.uid,
            authState.accountSettings,
            authState.userData?.webOnboardingHandoff
          );
          // A late checkpoint from an older client cannot reopen a web setup
          // that this session explicitly finished. Other onboarding is unchanged.
          if (completedWebSetup?.status === "complete") {
            return { showOnboarding: false, redirectToOnboarding: false };
          }

          console.log("[AppInitializer] charaterOnboarding - ACCOUNT DEBUG:", {
            accountId,
            selectedAccountId: authState.selectedAccountId,
            defaultAccountId: authState.userData?.defaultAccount?.id,
            userEmail: authState.userData?.email,
          });

          // If saved onboarding progress exists, the user started onboarding
          // but didn't finish. Force them back to /onboarding to resume,
          // regardless of whether they already have a character. Character
          // creation is step 2 of 6 — having a character doesn't mean done.
          // Only redirect for actual onboarding flows — non-onboarding flows
          // should not hijack navigation.
          if (savedProgress) {
            if (isOnboardingProgress(savedProgress)) {
              console.log(
                "[AppInitializer] charaterOnboarding - DECISION: saved onboardingProgress found, resuming onboarding",
                {
                  accountId,
                  flowType: savedProgress.flowType,
                  stepIndex: savedProgress.stepIndex,
                }
              );
              return {
                showOnboarding: true,
                redirectToOnboarding: true,
              };
            }
            // Unrecognized flow type. Previously this branch DELETED the
            // progress as "stale data", which destroyed the only record of
            // where the user was and made the state unrecoverable on the next
            // launch. Leave it alone and just don't redirect — a stale doc is
            // harmless, a deleted one is not.
            console.log(
              "[AppInitializer] charaterOnboarding - onboardingProgress is for a non-onboarding flow, ignoring (not deleting)",
              { accountId, savedFlowType: savedProgress.flowType }
            );
          }

          // No saved progress — check if user has any characters to decide
          // whether they need onboarding at all (brand-new user vs returning user)
          const hasCharacters = accountId
            ? await useCharactersStore.getState().checkHasCharacters(accountId)
            : false;

          // A user who completed onboarding and later DELETED all their
          // characters must not be forced back into onboarding — they land on
          // the empty roster with a "Create a New Character" CTA instead.
          const alreadyOnboarded =
            authState.accountSettings?.createdDefaultCharacter === true;
          const needsOnboarding = !hasCharacters && !alreadyOnboarded;

          console.log("[AppInitializer] charaterOnboarding - DECISION:", {
            accountId,
            hasCharacters,
            alreadyOnboarded,
            needsOnboarding,
            willGoTo: needsOnboarding ? "ONBOARDING" : "HOME",
          });

          return {
            showOnboarding: needsOnboarding,
            redirectToOnboarding: needsOnboarding,
          };
        },
      },
      {
        id: "deferredDeepLinks",
        label: "Preparing your destination...",
        check: async () => {
          if (!requiresAuth && !useAuthStore.getState().userData?.uid) {
            return;
          }

          const currentUserData = useAuthStore.getState().userData;

          if (!currentUserData) {
            return { hasDeferredDeepLink: false };
          }

          const deferredDeepLink = await getDeferredDeepLinkFromFirebase(
            currentUserData.uid
          );

          return {
            hasDeferredDeepLink: !!deferredDeepLink,
            destination: deferredDeepLink?.url,
          };
        },
      },
    ];

    return steps;
    // IMPORTANT: Do NOT include useAuthStore.getState().userData here!
    // The steps access current state via getState() inside check() functions.
    // Including it here causes re-render loops because getState() returns a new reference each render.
  }, [location.pathname, location.search, requiresAuth, isPublicRoute, supportsWebHandoff]);
  const [initError, setInitError] = useState<string | null>(null);

  // Update-specific state (from AutoUpdate.tsx logic)
  const prevRequiresAuthRef = useRef<boolean | null>(null);
  const isReinitializingRef = useRef<boolean>(false);
  const isRunningInitializeRef = useRef<boolean>(false);
  // Track if we've already started the initial run to prevent duplicate triggers
  const hasStartedInitRef = useRef(false);

  const currentStepIndex = lastCompletedStepIndexRef.current + 1;
  const currentStep = initSteps[currentStepIndex];
  const initProgress =
    initSteps.length > 0
      ? ((lastCompletedStepIndexRef.current + 1) / initSteps.length) * 100
      : 0;
  const currentStepLabel = currentStep?.label || "Initializing...";

  // Keep a ref to avoid stale closures in external callbacks
  const initStepsRef = useRef<InitStep[]>([]);
  useEffect(() => {
    initStepsRef.current = initSteps;
  }, [initSteps]);

  // Main initialization flow
  const runInitialization = async (startFromIndex: number = 0) => {
    isRunningInitializeRef.current = true;
    setInitializationRunning(true);
    console.log(
      `[AppInitializer] Starting initialization from step ${startFromIndex}...`
    );

    try {
      // Use initSteps directly if ref is empty (race condition on first render)
      const steps =
        initStepsRef.current.length > 0 ? initStepsRef.current : initSteps;
      console.log(
        `[AppInitializer] Steps count: ${steps.length}, startFromIndex: ${startFromIndex}`
      );
      let postInitNavigation = "";

      for (let i = startFromIndex; i < steps.length; i++) {
        const step = steps[i];

        console.log(
          `[AppInitializer] Running step: ${step.id}- ${i}/${steps.length - 1}`
        );

        try {
          // Run step
          const result = await step.check();

          console.log(`[AppInitializer] Step ${step.id} result:`, result);

          // Handle auth requirement BEFORE marking step complete
          if (step.id === "auth" && requiresAuth && result?.showAuthPanel) {
            // Show auth panel for unauthenticated users
            console.log(
              "[AppInitializer] Showing auth panel for authentication"
            );
            lastCompletedStepIndexRef.current = i;
            // Use flushSync to ensure the state update is processed immediately
            // This fixes an issue where native WebViews don't re-render properly
            flushSync(() => {
              setShowAuthDrawer(true);
            });
            return; // Stop initialization - auth panel will handle sign-in
          }

          if (result?.handoffBlocked) return;
          if (result?.handoffFallback) {
            // Don't follow a failed connection with more startup network work.
            // The already-onboarded account stays selected; retry is optional.
            if (routeRef.current !== "/")
              await navigate("/", { replace: true });
            flushSync(() => setIsInitialized(true));
            return;
          }
          if (result?.handoffDestination)
            postInitNavigation = result.handoffDestination;

          if (
            (step.id === "characterOnboarding" ||
              step.id === "webOnboardingHandoff") &&
            result?.showOnboarding
          ) {
            lastCompletedStepIndexRef.current = i;
            // Use flushSync to ensure the state update is processed immediately on native
            flushSync(() => {
              setShowOnboarding(true);
              if (result?.redirectToOnboarding) {
                setRedirectToOnboarding(true);
              }
            });
            // Don't set isInitialized - render will show onboarding via showOnboarding state
            return;
          }

          if (step.id === "deferredDeepLinks" && result?.hasDeferredDeepLink) {
            postInitNavigation = result.destination;
          }

          lastCompletedStepIndexRef.current = i;
        } catch (error: any) {
          // Handle special update-related errors
          if (
            error?.message === "INCOMPATIBLE_VERSION" ||
            error?.message === "VERSION_NOT_AVAILABLE"
          ) {
            return;
          }

          throw error;
        }
      }

      if (postInitNavigation) {
        console.log(
          "[AppInitializer] Post-init navigation:",
          postInitNavigation
        );
        flushSync(() => {
          setIsInitialized(true);
        });
        await navigate(postInitNavigation);
        return;
      }

      // All steps completed successfully - mark as initialized
      console.log("[AppInitializer] Setting isInitialized to true");
      flushSync(() => {
        setIsInitialized(true);
      });
      console.log("[AppInitializer] Initialization complete");
    } catch (error) {
      logException(error);
      console.error("[AppInitializer] Initialization failed:", error);
      setInitError(
        error instanceof Error ? error.message : "Initialization failed"
      );
    } finally {
      isRunningInitializeRef.current = false;
      setInitializationRunning(false);
    }
  };

  // Handle redirect to /onboarding route when there's saved progress
  useEffect(() => {
    if (redirectToOnboarding && showOnboarding) {
      navigate("/onboarding", { replace: true });
    }
  }, [redirectToOnboarding, showOnboarding, navigate]);

  // When navigating to /onboarding, clear auth drawer state since HybridFlowPage handles its own auth
  // This prevents stale showAuthDrawer=true from previous navigation blocking the onboarding UI
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;
    prevPathRef.current = currentPath;

    // If we just navigated TO /onboarding, clear any auth drawer state
    if (currentPath === "/onboarding" && prevPath !== "/onboarding") {
      console.log(
        "[AppInitializer] Navigated to /onboarding, clearing auth drawer state"
      );
      setShowAuthDrawer(false);
      setShowOnboarding(false);
      setRedirectToOnboarding(false);
    }

    // When returning from /onboarding via client-side navigation (e.g. after
    // flow completion or a race between navigate() and window.location.replace),
    // initialization was previously interrupted so isInitialized is still false
    // while hasStartedInitRef is true — the init effect won't re-trigger on its
    // own because isInitialized didn't change. Reset and restart.
    if (
      prevPath === "/onboarding" &&
      currentPath !== "/onboarding" &&
      !isInitialized &&
      !isRunningInitializeRef.current
    ) {
      console.log(
        "[AppInitializer] Returned from /onboarding, restarting initialization"
      );
      lastCompletedStepIndexRef.current = -1;
      setInitError(null);
      setUpdateStatus(undefined);
      setShowAuthDrawer(false);
      setShowOnboarding(false);
      setRedirectToOnboarding(false);
      hasStartedInitRef.current = true;
      runInitialization();
    }
  }, [location.pathname]);

  const handlePostInterruption = async () => {
    console.log("[AppInitializer] Handling post-interruption...");
    setShowAuthDrawer(false);
    setShowOnboarding(false);
    setRedirectToOnboarding(false);

    const currentLastCompletedIndex = lastCompletedStepIndexRef.current;
    const nextStepIndex = currentLastCompletedIndex + 1;
    console.log(
      `[AppInitializer] Continuing from step ${nextStepIndex} after interruption (lastCompleted was ${currentLastCompletedIndex})`
    );
    runInitialization(nextStepIndex);
  };

  const continueInitializationAfterAuth = async () => {
    await waitForState(useAuthStore, (state) => {
      const accountId =
        state.selectedAccountId || state.userData?.defaultAccount?.id;
      return !state.initializing && !!accountId;
    });
    await handlePostInterruption();
  };

  const redemptionSession = usePaymentsStore(
    (s) => s.webPurchaseRedemptionSession
  );
  const prevRedemptionSessionIdRef = useRef<number | null>(null);

  // Redemption dialog can complete sign-in outside AuthPanel; resume init when it closes.
  useEffect(() => {
    const sessionId = redemptionSession?.sessionId ?? null;
    const prevSessionId = prevRedemptionSessionIdRef.current;
    prevRedemptionSessionIdRef.current = sessionId;

    if (prevSessionId == null || sessionId != null) return;
    if (isInitialized || !showAuthDrawer) return;

    void (async () => {
      const { userData, user } = useAuthStore.getState();
      const signedIn = !!userData && user?.isAnonymous !== true;
      if (!signedIn) return;

      console.log(
        "[AppInitializer] Redemption dialog dismissed after sign-in, continuing initialization"
      );
      await continueInitializationAfterAuth();
    })();
  }, [redemptionSession, showAuthDrawer, isInitialized]);

  const resetInitialization = () => {
    lastCompletedStepIndexRef.current = -1;
    hasStartedInitRef.current = false;
    setInitError(null);
    setUpdateStatus(undefined);
    setIsInitialized(false);
    setHandoffChecked(!supportsWebHandoff);
    setHandoffBlocked(false);
    setHandoffProblem(undefined);
    setShowAuthDrawer(false);
    setShowOnboarding(false);
    setRedirectToOnboarding(false);
  };

  // Centralized helper to request a safe re-initialization
  const requestReinit = (reason: string) => {
    console.log(`[AppInitializer] Re-init requested: ${reason}`);
    if (updateStatus?.isApplying) {
      console.log("[AppInitializer] Re-init skipped: update is applying");
      return;
    }
    if (!isInitialized) {
      // Already initializing or not yet initialized; let existing flow proceed
      return;
    }
    if (isReinitializingRef.current) {
      return;
    }
    isReinitializingRef.current = true;
    resetInitialization();
    // Let the existing effect that watches initialization state trigger runInitialization
    // Release the guard on the next tick to allow future re-inits
    setTimeout(() => {
      isReinitializingRef.current = false;
    }, 0);
  };

  const serverHandoff = useAuthStore(
    (state) => state.userData?.webOnboardingHandoff
  );
  const accountHandoff = useAuthStore(
    (state) => state.accountSettings?.webOnboardingHandoff
  );
  const signedInUid = useAuthStore((state) =>
    state.user?.isAnonymous ? undefined : state.user?.uid
  );
  const unfinishedAccountId = useAuthStore((state) => {
    if (!selectAccountSettingsLoaded(state)) return;
    const handoff = accountWebHandoff(
      state.accountId,
      state.user?.uid,
      state.accountSettings,
      state.userData?.webOnboardingHandoff
    );
    const unfinished = handoff
      ? !webHandoffIsComplete(handoff, state.accountSettings)
      : state.accountSettings?.createdDefaultCharacter !== true;
    return unfinished ? state.accountId : undefined;
  });
  const candidate = supportsWebHandoff
    ? handoffIntent() || (signedInUid ? pendingWebHandoff() : undefined)
    : undefined;
  const candidateId = candidate ? webHandoffId(candidate) : undefined;
  const retryHandoff = () => {
    if (
      (!candidate && !handoffProblem) ||
      !canResumeHandoff() ||
      isRunningInitializeRef.current
    )
      return;
    if (
      !handoffProblem &&
      handoffChecked &&
      location.pathname === "/onboarding" &&
      attemptedHandoffRef.current === candidateId &&
      useAuthStore.getState().accountId === candidate?.accountId
    ) {
      // Capacitor and AppsFlyer can both deliver the same tap. The matching
      // onboarding page is already continuing this session.
      clearWebHandoffIntent();
      return;
    }
    attemptedHandoffRef.current = undefined;
    if (isInitialized) requestReinit("continue web setup");
    else {
      resetInitialization();
      hasStartedInitRef.current = true;
      void runInitialization();
    }
  };
  const retryRef = useRef(retryHandoff);
  retryRef.current = retryHandoff;
  useEffect(() => {
    if (!supportsWebHandoff) return;
    const wake = () => setHandoffWake((value) => value + 1);
    window.addEventListener("online", wake);
    window.addEventListener(WEB_HANDOFF_LINK_EVENT, wake);
    const listener = Capacitor.isNativePlatform()
      ? CapacitorApp.addListener("appStateChange", ({ isActive }) => {
          if (isActive) wake();
        })
      : undefined;
    return () => {
      window.removeEventListener("online", wake);
      window.removeEventListener(WEB_HANDOFF_LINK_EVENT, wake);
      void listener?.then((handle) => handle.remove());
    };
  }, [supportsWebHandoff]);
  const lastWakeRef = useRef(0);
  const lastHandoffPathRef = useRef(location.pathname);
  useEffect(() => {
    const returnedHome =
      lastHandoffPathRef.current !== "/" && location.pathname === "/";
    lastHandoffPathRef.current = location.pathname;
    const woke = handoffWake !== lastWakeRef.current;
    lastWakeRef.current = handoffWake;
    if (
      (!candidateId && !handoffProblem) ||
      isPublicRoute ||
      !canResumeHandoff() ||
      isRunningInitializeRef.current
    )
      return;
    const untried = !!candidateId && attemptedHandoffRef.current !== candidateId;
    // Recover an unfinished account already on Home, but never switch an
    // established user's account just because a late snapshot arrived.
    const unfinishedHome =
      isInitialized &&
      location.pathname === "/" &&
      !!unfinishedAccountId &&
      candidate?.accountId === unfinishedAccountId;
    if (
      woke ||
      (untried && unfinishedHome) ||
      (returnedHome && untried) ||
      (untried &&
        handoffChecked &&
        location.pathname === "/onboarding" &&
        !flowEngagedRef.current)
    ) {
      retryRef.current();
    }
  }, [
    serverHandoff,
    accountHandoff,
    signedInUid,
    candidateId,
    handoffWake,
    location.pathname,
    handoffChecked,
    handoffProblem,
    initializationRunning,
    isInitialized,
    unfinishedAccountId,
    isPublicRoute,
  ]);

  const handoffNotice =
    (candidate || handoffProblem) && (handoffProblem || isInitialized) ? (
      <div
        role="status"
        className="order-first m-4 rounded-xl border border-purple-200 bg-purple-50 p-4 text-sm text-gray-800"
      >
        <p>
          {handoffProblem === "update-required"
            ? "Your saved web setup needs a newer version of the app. Update the app, then try again."
            : handoffProblem === "recovery-required"
              ? "We found your web setup, but couldn’t restore where you left off. Please try again. If this continues, contact support."
            : handoffProblem === "retry"
              ? candidate
                ? "We couldn’t load your saved web setup. Your progress is saved. Please check your connection and try again."
                : "We couldn’t check for your saved setup. Please check your connection and try again."
              : "Your web setup is saved and ready to continue."}
        </p>
        <button
          type="button"
          className="mt-2 font-medium text-purple-700 underline"
          onClick={retryHandoff}
        >
          {handoffProblem ? "Try again" : "Continue setup"}
        </button>
        {handoffProblem === "update-required" && (
          <a
            className="ml-4 text-purple-700 underline"
            href={storeLinks[Capacitor.getPlatform() as "ios" | "android"]}
          >
            Update app
          </a>
        )}
      </div>
    ) : null;

  useEffect(() => {
    const savedUrl = localStorage.getItem("pendingUpdateUrl");
    if (savedUrl) {
      console.log("[AppInitializer] Restoring URL after update:", savedUrl);
      localStorage.removeItem("pendingUpdateUrl");

      try {
        const url = new URL(savedUrl);
        const pathWithParams = url.pathname + url.search + url.hash;

        if (
          pathWithParams !==
          location.pathname + location.search + location.hash
        ) {
          navigate(pathWithParams, { replace: true });
        }
      } catch (error) {
        console.error("[AppInitializer] Failed to restore URL:", error);
      }
    }
  }, []); // Run only once on mount

  useEffect(() => {
    // Only start initialization once per mount
    // The guards prevent re-running if already in progress or completed
    if (
      !isInitialized &&
      !hasStartedInitRef.current &&
      !isRunningInitializeRef.current &&
      initSteps.length > 0
    ) {
      hasStartedInitRef.current = true;
      runInitialization();
    }
    // Note: We intentionally only check isInitialized here, not initSteps.length
    // The steps are defined once and their check() functions get fresh state via getState()
  }, [isInitialized]);

  useEffect(() => {
    const { currentBundle, currentBuild, isInitialized } =
      useAutoUpdateStore.getState();

    const minVersion = webHandoffIntent(location.search)
      ? null
      : new URLSearchParams(location.search).get("minVersion");

    const { meetsMinVersion } = compareToMinVersion(
      currentBundle?.version,
      minVersion
    );

    if (!meetsMinVersion) {
      requestReinit("minVersion changed in URL");
    }
  }, [location.search, isInitialized]);

  // Re-init trigger: when navigating into a route that requires auth while unauthenticated
  useEffect(() => {
    const routeRequiresAuth = matches.some(
      (match) => (match.handle as { requiresAuth: boolean })?.requiresAuth
    );
    const userData = useAuthStore.getState().userData;
    if (
      isInitialized &&
      routeRequiresAuth &&
      !userData &&
      prevRequiresAuthRef.current !== true
    ) {
      requestReinit("entered requiresAuth route without authentication");
    }
    prevRequiresAuthRef.current = routeRequiresAuth;
  }, [matches, isInitialized]);

  // Re-init trigger: when user signs out (userData becomes null after being set)
  // This ensures the auth panel is shown again after sign-out
  const prevUserDataRef = useRef<any>(undefined);
  useEffect(() => {
    const unsubscribe = useAuthStore.subscribe((state) => {
      const wasAuthenticated =
        prevUserDataRef.current !== undefined &&
        prevUserDataRef.current !== null;
      const isNowUnauthenticated =
        state.userData === null && !state.initializing;

      if (wasAuthenticated && isNowUnauthenticated && isInitialized) {
        console.log("[AppInitializer] User signed out, re-initializing...");
        requestReinit("user signed out");
      }

      prevUserDataRef.current = state.userData;
    });

    return () => unsubscribe();
  }, [isInitialized]);

  if (!isInitialized) {
    if (handoffBlocked && !isPublicRoute) return handoffNotice;
    if (updateStatus?.versionNotAvailable || updateStatus?.isIncompatible) {
      return (
        <div className="flex h-full min-h-0 w-full flex-1 items-center justify-center">
          <div className="flex flex-col gap-4 text-brand-secondary text-lg font-medium items-center">
            <div className="text-brand-secondary text-lg font-medium">
              {updateStatus?.isIncompatible
                ? "An update is required to view this content."
                : "The required app version is not available."}
            </div>

            {updateStatus?.isIncompatible && (
              <Button className="w-min px-8" variant="outline" asChild>
                <Link
                  to={storeLinks[Capacitor.getPlatform() as "ios" | "android"]}
                >
                  Download the latest version
                </Link>
              </Button>
            )}

            {/* {window.history.length > 1 ? (
              <Button
                className="w-min px-8"
                variant="outline"
                onClick={() => {
                  navigate(-1);
                  resetInitialization();
                  runInitialization();
                }}
              >
                Go back
              </Button>
            ) : ( */}
            <Button
              className="w-min px-8"
              variant="outline"
              onClick={() => {
                navigate("/", { replace: true });
              }}
            >
              Go home
            </Button>
            {/* )} */}
          </div>
        </div>
      );
    }

    // Public routes handle their own UX during init — render them immediately
    // instead of the loading gate. Covers all sharedPublicRoutes plus onboarding.
    if (
      isPublicRoute ||
      (location.pathname === "/onboarding" &&
        (flowEngagedRef.current ||
          (!(supportsWebHandoff && handoffIntent() && !signedInUid) &&
            (handoffChecked ||
              (!candidate && (!supportsWebHandoff || !signedInUid))))))
    ) {
      return (
        <div className="flex h-full min-h-0 w-full flex-1 flex-col">
          <RouteOutlet />
        </div>
      );
    }

    if (showOnboarding) {
      // Redirect to /onboarding route - useEffect handles navigation
      return null;
    }

    // Normal initialization screen
    const { availableBundle, nextBundle, currentBundle } =
      useAutoUpdateStore.getState();
    return (
      <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain bg-gradient-to-b from-purple-50 to-white">
          {/* Inner min-h-full + justify-center: center when short; bounded outer flex-1 min-h-0 makes this the scrollport (not document). */}
          <div className="flex min-h-full w-full flex-col items-center justify-center px-6 py-8">
            <div className="flex w-full max-w-sm flex-col items-center gap-8">
              <img
                src="/assets/brain-flower-logo.svg"
                alt="Unique Minds"
                className="h-30"
              />

              {showAuthDrawer ? (
                <AuthPanel
                  showLogo={false}
                  title=""
                  description="Sign in to your Unique Minds account"
                  enableCreateAnonymous={true}
                  enableAppDownloadPrompt={false}
                  onAuthenticated={continueInitializationAfterAuth}
                />
              ) : (
                <div className="flex w-full flex-col items-center gap-4">
                  <div className="w-full">
                    <Progress value={initProgress} className="h-2" />
                  </div>

                  <p className="text-center text-sm text-gray-600">
                    {updateStatus?.isApplying && updateStatus?.updateVersion
                      ? `Applying update to version ${updateStatus?.updateVersion}...`
                      : updateStatus?.isDownloading
                        ? `Downloading version ${updateStatus?.updateVersion}...`
                        : currentStepLabel || "Initializing..."}
                  </p>
                </div>
              )}
              {initError &&
                !initError.includes("VERSION") &&
                !initError.includes("INCOMPATIBLE") && (
                  <div className="w-full rounded-lg border border-red-200 bg-red-50 p-4">
                    <p className="text-sm text-red-600">{initError}</p>
                    <button
                      onClick={() => {
                        resetInitialization();
                        runInitialization();
                      }}
                      className="mt-2 text-sm text-red-700 underline"
                    >
                      Try again
                    </button>
                  </div>
                )}
            </div>
          </div>

          {/* <div className="fixed bottom-4 right-8 flex flex-col items-center gap-4 text-grey-400 font-light text-sm">
            {availableBundle ? `(availible: ${availableBundle.version}) ` : ""}
            {nextBundle ? `(next: ${nextBundle.version}) ` : ""} --
            {currentBundle?.version}
          </div> */}
        </div>
      </div>
    );
  }

  // Render the app once initialized
  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col">
      <RouteOutlet />
      {location.pathname === "/" && handoffNotice}
    </div>
  );
}
